import type { InventoryVehicle } from '@carvana/merch-contracts';
import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile/utils/availability';
import { datadogRum } from '@datadog/browser-rum';
import { type AvailabilityData } from '~/context/srp/InventoryContext';
import type { MegaEventVehicle } from '~/contracts/common';
import getMegaEventVehicleAvailability from '~/utilities/getMegaEventVehicleAvailability';
import getPurchaseStatus from '~/utilities/getPurchaseStatus';
import replaceUndefinedWithNull from '~/utilities/replaceUndefinedWithNull';

const defaultAvailability = {
  soonestDelivery: { content: undefined },
  soonestPickup: { content: undefined }
};

export default function getVehicleData(
  latestVehicles: InventoryVehicle[],
  inventoryBulkData: Record<number, AvailabilityData>
) {
  return latestVehicles.map<MegaEventVehicle>((v, tileIndex) => {
    const {
      apr,
      discounts,
      downPaymentDetails,
      estimatedTaxesAndFees,
      feesMinusShipping,
      monthlyPayment,
      pricingErrorMessage,
      tradeTaxSavings,
      term,
      total,
      totalTaxes
    } = v.price ?? {};

    const {
      isPurchasePending,
      locationId,
      offering,
      previousPrice,
      stockNumber,
      stores,
      transportCost,
      vehicleId,
      vehicleInventoryType,
      vehicleLockType,
      vehiclePurchaseType,
      vehicleUnlockDateTime
    } = v;

    const { soonestDelivery, soonestPickup } =
      inventoryBulkData[v.vehicleId] ?? defaultAvailability;
    const { activityType, getItBy, routeId, utcSoonestDateTimeIso } =
      calculateGetItBy(
        calculateSoonestAvailable({
          delivery: soonestDelivery.content,
          pickup: soonestPickup.content
        })
      );

    if (
      (soonestDelivery.content?.activityType ||
        soonestPickup.content?.activityType) &&
      !activityType
    ) {
      datadogRum.addAction(
        'null-tile-info-activityType-with-valid-delivery-or-pickup',
        {
          deliveryInfo: soonestDelivery.content,
          pickupInfo: soonestPickup.content,
          tileInfoActivityType: activityType
        }
      );
    }

    const { isLocked, isOnHold } = getPurchaseStatus(v);
    const totalCashDue = v.price?.downPaymentDetails?.totalCashDue;
    const hasTerms = totalCashDue != null && monthlyPayment != null;

    return replaceUndefinedWithNull({
      deliveryInfo: getMegaEventVehicleAvailability(soonestDelivery.content),
      factoryUpgradesTotal: v.factoryUpgrades?.factoryUpgradesMsrp,
      hasSplattingVideo: Boolean(v.splattingVideos?.length),
      isPurchasePending,
      locationId,
      offering,
      pickupInfo: getMegaEventVehicleAvailability(soonestPickup.content),
      stockNumber,
      stores,
      tileIndex,
      tileInfo: {
        activityType,
        additionalCashDown: downPaymentDetails?.additionalCashDown,
        apr,
        discounts,
        estimatedTaxesAndFees,
        feesMinusShipping,
        getItBy,
        hasTerms,
        isLocked,
        isOnHold,
        monthlyPayment,
        nonRefundableShippingFees:
          downPaymentDetails?.nonRefundableShippingFees,
        previousPrice,
        pricingErrorMessage,
        routeId,
        term,
        total,
        totalCashDue,
        totalTaxes,
        transportCost,
        utcSoonestDateTimeIso
      },
      tradeTaxSavings,
      vehicleId,
      vehicleInventoryType,
      vehicleLockType,
      vehiclePurchaseType,
      vehicleUnlockDateTime
    } satisfies MegaEventVehicle);
  });
}
